import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import './BadgeGenerator.css';

const BadgeGenerator = () => {
  const [username, setUsername] = useState('');
  const [pfpUrl, setPfpUrl] = useState('');
  const [selectedColor, setSelectedColor] = useState('#B753FF');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedAccess, setSelectedAccess] = useState([]);
  const fileInputRef = useRef(null);

  const teamColors = [
    { color: '#B753FF', title: 'Purple Team' },
    { color: '#FF955E', title: 'Orange Team' },
    { color: '#B0FF6F', title: 'Green Team' },
    { color: '#61C3FF', title: 'Blue Team' },
    { color: '#FF54D7', title: 'Pink Team' },
  ];

  const roleCategories = [
    {
      title: 'Entry Level',
      roles: ['Lets Pruv It', 'Proof Verified', 'ZK Frens']
    },
    {
      title: 'Specialized Proofs',
      roles: ['Proof of Art', 'Proof of Video', 'Proof of Dev', 'Proof of Writing', 'Proof of Music']
    },
    {
      title: 'L1 - Entry Provers',
      roles: ['Proved Ur LUV', 'Super Prover Potential', 'Programmable Truth Potential']
    },
    {
      title: 'L2 - Active Provers',
      roles: ['Proofer', 'Truth Prover', 'SP1up Prover', 'Prover']
    },
    {
      title: 'L3 - Elite Provers',
      roles: ['Programmable Truth',  'Cargo Prover', 'Super Prover', 'ALL IN SUCCINCT', 'PWOOFER']
    },
    {
      title: 'Community & Staff',
      roles: ['Regional Helper', 'Helper Prover', 'Staff Prover', 'Dev Helper']
    }
  ];

  const accessLevels = ['Stage 1', 'Stage 2', 'Stage 2.5'];

  const getTitleFromRoles = (roles) => {
    const rolePoints = {
      'ALL IN SUCCINCT': 100,
      'PWOOFER': 95,
      'Super Prover': 90,
      'Programmable Truth Cargo Prover': 85,
      'Prover': 70,
      'SP1up Prover': 65,
      'Truth Prover': 60,
      'Proofer': 55,
      'Programmable Truth Potential': 45,
      'Super Prover Potential': 40,
      'Proved Ur LUV': 35,
      'Staff Prover': 80,
      'Helper Prover': 50,
      'Regional Helper': 45,
      'Retired Prover': 75,
      'Proof of Dev': 30,
      'Proof of Art': 25,
      'Proof of Video': 25,
      'Proof of Writing': 25,
      'Proof of Music': 25,
      'ZK Frens': 15,
      'Proof Verified': 10,
      'Pruv It': 5
    };

    const totalPoints = roles.reduce((sum, role) => sum + (rolePoints[role] || 0), 0);
    const roleCount = roles.length;

    if (totalPoints >= 200 || roles.includes('ALL IN SUCCINCT')) {
      return 'Succinct Overlord';
    } else if (totalPoints >= 150 || roles.includes('PWOOFER')) {
      return 'Succinct Legend';
    } else if (totalPoints >= 120 || roles.includes('Super Prover')) {
      return 'Succinct Maxi';
    } else if (totalPoints >= 90 || roles.includes('Staff Prover')) {
      return 'Succinct Pro';
    } else if (totalPoints >= 60 || roleCount >= 5) {
      return 'Succinct Veteran';
    } else if (totalPoints >= 35 || roleCount >= 3) {
      return 'Succinct Builder';
    } else if (totalPoints >= 15 || roleCount >= 1) {
      return 'Succinct Prover';
    } else {
      return null;
    }
  };

  const userTitle = getTitleFromRoles(selectedRoles);

  const handlePFPUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPfpUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleAccessToggle = (access) => {
    setSelectedAccess(prev =>
      prev.includes(access)
        ? prev.filter(a => a !== access)
        : [...prev, access]
    );
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const downloadBadge = () => {
    const badgeElement = document.querySelector('.badge');
    if (!badgeElement) {
      alert('Badge element not found');
      return;
    }

    html2canvas(badgeElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: null
    }).then((canvas) => {
      const link = document.createElement('a');
      link.download = 'succinct_badge.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  const shareOnX = () => {
   
    const text = `Just generated my @SuccinctLabs community badge! 🚀 create yours at https://succinctbadge.vercel.app
    Proving the future with Zero-Knowledge!`;

    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(tweetUrl, '_blank');
  };

  return (
    <div className="badge-generator">
      <div className="background">
        <div className="floating-shapes">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="shape"></div>
          ))}
        </div>
      </div>

      <div className="container">
        <div className="header">
          <h1>Succinct Labs Badge Generator</h1>
          <p className="subtitle">Create your personalized community badge and flex your ZK proof prowess! 🚀</p>
        </div>

        <div className="main-content">
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="username">X Username:</label>
              <input
                type="text"
                id="username"
                placeholder="@yourhandle"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="pfp">Profile Picture:</label>
              <input
                type="file"
                id="pfp"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePFPUpload}
              />
            </div>

            <div className="form-group">
              <label>Team Color:</label>
              <div className="team-colors">
                {teamColors.map((team, index) => (
                  <div
                    key={index}
                    className={`color-option ${selectedColor === team.color ? 'selected' : ''}`}
                    style={{ background: team.color }}
                    title={team.title}
                    onClick={() => setSelectedColor(team.color)}
                  ></div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Access Levels:</label>
              <div className="access-levels">
                {accessLevels.map((access, index) => (
                  <div key={index} className="access-item">
                    <input
                      type="checkbox"
                      id={`access-${index}`}
                      checked={selectedAccess.includes(access)}
                      onChange={() => handleAccessToggle(access)}
                    />
                    <label htmlFor={`access-${index}`}>{access}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Discord Roles:</label>
              <div className="roles-section">
                {roleCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="role-category">
                    <h4>{category.title}</h4>
                    {category.roles.map((role, roleIndex) => (
                      <div key={roleIndex} className="role-item">
                        <input
                          type="checkbox"
                          id={`role-${categoryIndex}-${roleIndex}`}
                          checked={selectedRoles.includes(role)}
                          onChange={() => handleRoleToggle(role)}
                        />
                        <label htmlFor={`role-${categoryIndex}-${roleIndex}`}>{role}</label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="badge-preview">
            <div
              className="badge enhanced-badge"
              style={{
                background: `linear-gradient(135deg, ${hexToRgba(selectedColor, 0.2)}, ${hexToRgba(selectedColor, 0.1)})`,
                border: `2px solid ${hexToRgba(selectedColor, 0.8)}`,
                minHeight: selectedRoles.length > 12 ? '550px' : '450px'
              }}
            >
              <div className="badge-content">
                <div className="badge-header">
                  <div className="badge-logo">SUCCINCT LABS</div>
                </div>

                <div className="pfp-container" style={{ borderColor: selectedColor }}>
                  {pfpUrl ? (
                    <img src={pfpUrl} alt="pfp" className="pfp-img" />
                  ) : (
                    <div className="pfp-placeholder">Upload PFP</div>
                  )}
                </div>

                <div className="username-container">
                  <div className="username">{username || '@username'}</div>
                  {userTitle && <div className="user-title">{userTitle}</div>}
                </div>

                <div className="badge-roles enhanced-roles">
                  <div 
                    className="role-badges"
                    style={{ 
                      maxHeight: selectedRoles.length > 12 ? '200px' : 'none',
                      overflowY: selectedRoles.length > 12 ? 'auto' : 'visible',
                      paddingRight: selectedRoles.length > 12 ? '5px' : '0'
                    }}
                  >
                    {selectedRoles.map((role, index) => (
                      <div
                        key={index}
                        className="role-badge"
                        style={{
                          background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
                          border: '2px solid #444444',
                          color: '#ffffff',
                          fontWeight: '700',
                          fontSize: '12px',
                          textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
                          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          opacity: '1',
                          filter: 'contrast(1.2)'
                        }}
                      >
                        {role}
                      </div>
                    ))}
                  </div>

                  <div className="access-badges">
                    {selectedAccess.map((access, index) => (
                      <div 
                        key={index} 
                        className="access-badge"
                        style={{
                          background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
                          border: '2px solid #444444',
                          color: '#ffffff',
                          fontWeight: '700',
                          fontSize: '12px',
                          textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
                          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          opacity: '1',
                          filter: 'contrast(1.2)'
                        }}
                      >
                        {access}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="badge-footer">
                  Powered by SP1 ⚡ Zero-Knowledge Proofs
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <button className="btn btn-primary" onClick={downloadBadge}>
                 Download Badge
              </button>
              <button className="btn btn-secondary" onClick={shareOnX}>
                 Share on X
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeGenerator;